<template>
  <section id="search" class="hero-section matrix-surface">
    <div class="container">
      <div class="hero-panel">
        <div class="hero-copy">
          <p class="anniversary-label eyebrow">RAREPEPE · 10th anniversary</p>
          <h1>10 years of<br><span>CryptoArt<br>on Bitcoin.</span></h1>
          <p class="hero-intro">The Nakamoto Card turns ten. 300 original cards, each a unique NFT with its own history on Bitcoin. Find yours. Follow its story.</p>
          <div class="hero-links"><router-link class="pixel-button dark" to="/collection">Explore the 300 <span>↗</span></router-link><a class="text-link" href="#rarepepe">Back to the beginning ↓</a></div>
          <form class="search-form" @submit.prevent="performSearch" autocomplete="off">
            <label class="eyebrow" for="naka-search">Find your Naka</label>
            <div class="search-field">
              <input id="naka-search" v-model="searchQuery" @input="updateSuggestions" @keydown.esc="suggestions = []" type="search" placeholder="Card number, BTC or ETH address" aria-describedby="search-help" />
              <button type="submit" aria-label="Find Naka"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="10" cy="10" r="6"/><path d="m15 15 6 6"/></svg></button>
            </div>
            <ul id="address-suggestions" v-if="suggestions.length" class="suggestions">
              <li v-for="suggestion in suggestions.slice(0, 5)" :key="suggestion"><button type="button" @click="selectSuggestion(suggestion)">{{ suggestion }}</button></li>
            </ul>
            <p id="search-help" class="search-note">Try #90, a Bitcoin address, or the Ethereum address associated with an Emblem vault.</p>
            <p v-if="noResult" class="search-message" role="status">No current Nakas found for this address.</p>
            <p v-if="lookupMessage" class="search-message" role="status">{{ lookupMessage }}</p>
            <div v-if="searchResults.length" class="search-results" aria-live="polite">
              <p>{{ searchResults.length }} {{ searchResults.length === 1 ? 'Naka' : 'Nakas' }} at the current numbered checkpoint</p>
              <div class="result-cards"><router-link v-for="cardId in searchResults" :key="cardId" :to="'/card/' + cardId"><img class="pixelated" :src="getPFPUrl('#' + cardId)" :alt="'Naka #' + cardId"><span>#{{ cardId }}</span></router-link></div>
            </div>
          </form>
        </div>
        <figure class="hero-art">
          <div class="anniversary-seal" aria-label="10 years, 2016 to 2026"><strong>10</strong><span>YEARS</span><small>2016—2026</small></div>
          <img src="@assets/img/RAREPEPE.svg" alt="The original RAREPEPE Nakamoto Card, Series 1 Card 1"/>
          <figcaption><span class="eyebrow">The first Rare Pepe</span><span>Series 1 · Card 1 · September 2016</span><span class="art-dates">Born on Bitcoin. Still rare.</span></figcaption>
        </figure>
      </div>
      <div class="feature-grid">
        <a class="feature history" href="#provenance"><div class="stack-art" aria-hidden="true"><span>#003</span><span>#002</span><span>#001</span></div><div><p class="eyebrow">RAREPEPE are NFTs too</p><h2>300 cards.<br>Each one unique.</h2><p>Numbering follows each card through Bitcoin history, giving every Nakamoto its own identity and provenance.</p></div><span class="feature-arrow" aria-hidden="true">↗</span></a>
        <router-link class="feature portraits" to="/card/90"><div class="portrait-pair"><img src="@assets/img/RAREPEPE.svg" alt="RAREPEPE card"><img v-if="getPFPUrl('#90')" class="pixelated" :src="getPFPUrl('#90')" alt="The portrait representing RAREPEPE number 90"></div><div><p class="eyebrow">Meet Nakamoto #90</p><h2>Your card.<br>Its portrait.</h2><p>Each PFP represents one specific Nakamoto card. A unique face for the piece of CryptoArt you own.</p></div><span class="feature-arrow" aria-hidden="true">↗</span></router-link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import useCollectionVaults from '@/composables/useCollectionVaults';
import { cardsForAddress } from '@/services/vaults.mjs';
import useNakaData from '@/composables/useNakaData';
const { matches, locations, records, ready, loading, error } = useCollectionVaults();
const { getPFPUrl } = useNakaData();
const router = useRouter();
const searchQuery = ref('');
const suggestions = ref([]);
const submitted = ref('');
const searchResults = computed(() => cardsForAddress(submitted.value, locations.value, matches.value));
const lookupReady = computed(() => /^0x/i.test(submitted.value) ? ready.value : !!locations.value);
const noResult = computed(() => submitted.value && lookupReady.value && !searchResults.value.length);
const lookupMessage = computed(() => !submitted.value || lookupReady.value ? '' : loading.value ? 'Checking current card locations and vaults…' : error.value || 'Address lookup is temporarily unavailable.');
function updateSuggestions() {
  submitted.value = '';
  const q = searchQuery.value.trim();
  const addresses = [...new Set([...Object.values(locations.value || {}).filter(l => l.kind === 'address').map(l => l.id), ...(records.value || []).map(v => v.owner).filter(Boolean)])];
  suggestions.value = q.length < 3 ? [] : addresses.filter(a => a.startsWith('0x') ? a.toLowerCase().startsWith(q.toLowerCase()) : a.startsWith(q)).slice(0, 5);
}
function selectSuggestion(suggestion) { searchQuery.value = suggestion; performSearch(); }
function performSearch() {
  const query = searchQuery.value.trim(); suggestions.value = []; submitted.value = '';
  if (!query) return;
  if (/^#?\d+$/.test(query)) {
    const serial = Number(query.replace('#', ''));
    if (serial >= 1 && serial <= 300) { router.push(`/card/${serial}`); return; }
  }
  submitted.value = query;
}
watch(searchQuery, value => { if (!value) { suggestions.value = []; submitted.value = ''; } });
</script>

<style scoped>
.hero-section { padding: 12px 0 56px; }
.hero-panel { background: linear-gradient(#92c544ed, #92c544ed), url('~@assets/img/rare-repeat-green.svg') repeat; box-shadow: var(--pixel-shadow); padding: 44px 46px; display: grid; grid-template-columns: minmax(0,1fr) 310px; gap: 48px; align-items: center; }
.anniversary-label { display: inline-block; background: #f5f2c9; color: var(--forest); font-size: 10px; padding: 8px 12px; box-shadow: 3px 3px 0 var(--forest); margin-bottom: 25px; }
h1 { font-size: 29px; color: var(--forest); margin: 0 0 22px; line-height: 1.12; }
h1 span { display: inline-block; margin-top: 6px; font-size: clamp(40px,4.7vw,62px); letter-spacing: -.05em; }
.hero-intro { max-width: 465px; color: #203610; font-size: 19px; line-height: 1.5; }
.hero-links { display: flex; align-items: center; flex-wrap: wrap; gap: 20px; margin-top: 25px; }
.hero-links .pixel-button { font-size: 11px; padding: 12px 16px; }
.hero-links .text-link { font-size: 9px; color: #244a17; }
.search-form { margin-top: 30px; padding-top: 22px; border-top: 1px solid #709735; position: relative; max-width: 465px; }
.search-form label { display: block; margin-bottom: 9px; color: #203610; }
.search-field { display: flex; background: white; box-shadow: var(--pixel-shadow); }
.search-field input { background: none; border: 0; width: 100%; min-width: 0; padding: 13px 15px; font-size: 14px; color: var(--ink); }
.search-field button { background: none; border: 0; color: var(--green); width: 48px; flex-shrink: 0; }
.search-field svg { width: 20px; }
.search-note { font-size: 12px; margin-top: 14px; color: #283c13; }
.suggestions { position: absolute; z-index: 5; left: 0; right: 0; margin: 8px 0 0; padding: 0; list-style: none; background: white; box-shadow: var(--pixel-shadow); }
.suggestions button { width: 100%; background: none; border: 0; text-align: left; padding: 12px; overflow-wrap: anywhere; color: var(--forest); font-size: 13px; }
.suggestions button:hover { background: var(--pale); }
.search-message,.search-results { background: var(--pale); padding: 12px; margin-top: 16px; font-size: 14px; }
.result-cards { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.result-cards a { width: 64px; position: relative; }
.result-cards span { position: absolute; bottom: 0; left: 0; background: white; font: 11px var(--font-titles); padding: 3px; }
.hero-art { position: relative; margin: 0; padding-top: 60px; text-align: center; }
.hero-art > img { width: 238px; transform: rotate(-4deg); filter: drop-shadow(6px 7px 0 #204b13); }
.hero-art figcaption { display: grid; gap: 7px; color: #244a17; margin-top: 27px; font-size: 12px; }
.hero-art figcaption .eyebrow { font-size: 11px; }
.art-dates { margin-top: 8px; font-size: 14px; }
.anniversary-seal { position: absolute; top: -6px; right: -14px; z-index: 1; display: grid; justify-items: center; gap: 2px; background: #f5f2c9; border: 2px solid var(--forest); box-shadow: 4px 5px 0 var(--forest); padding: 11px 14px; color: var(--forest); transform: rotate(7deg); font: 13px/1.2 var(--font-titles); }
.anniversary-seal strong { font-size: 55px; font-weight: 400; letter-spacing: -.08em; }
.anniversary-seal small { font-size: 9px; margin-top: 5px; }
.feature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 28px; }
.feature { position: relative; display: flex; gap: 24px; align-items: center; padding: 27px; box-shadow: var(--pixel-shadow); color: var(--ink); transition: transform .15s; }
.feature:hover { color: var(--ink); transform: translateY(-3px); }
.portraits { background: #d6bfef; }
.history { background: #ece5a6; }
.feature .eyebrow { font-size: 9px; margin-bottom: 8px; }
.feature h2 { font-size: 18px; margin-bottom: 8px; max-width: 280px; }
.feature p:not(.eyebrow) { font-size: 15px; line-height: 1.5; max-width: 310px; }
.feature-arrow { position: absolute; top: 12px; right: 14px; font-size: 20px; }
.portrait-pair { position: relative; width: 112px; height: 114px; flex-shrink: 0; }
.portrait-pair img { width: 72px; box-shadow: 3px 3px 0 #684487; position: absolute; top: 0; left: 0; }
.portrait-pair img + img { width: 70px; border: 3px solid white; top: 52px; left: 38px; }
.stack-art { position: relative; width: 80px; height: 90px; flex-shrink: 0; }
.stack-art span { display: grid; place-items: center; width: 62px; height: 72px; border: 2px solid #696326; background: #fcf9dc; font: 14px var(--font-titles); position: absolute; top: 0; left: 16px; }
.stack-art span:nth-child(2) { top: 8px; left: 8px; background: #e1dc86; }
.stack-art span:nth-child(3) { top: 16px; left: 0; background: #c6ca64; }
@media (max-width: 1000px) {
  .hero-panel { grid-template-columns: minmax(0,1fr) 230px; padding: 32px; gap: 30px; }
  .hero-art > img { width: 195px; }
  .anniversary-seal { right: -6px; }
  .feature { gap: 18px; padding: 24px 20px; }
  .portrait-pair { width: 92px; }
  .portrait-pair img { width: 60px; }
  .portrait-pair img + img { width: 60px; left: 30px; }
}
@media (max-width: 700px) {
  .hero-section { padding-bottom: 36px; }
  .hero-panel { grid-template-columns: 1fr; gap: 22px; padding: 26px 22px; }
  .hero-copy { display: contents; }
  h1 { order: 1; font-size: 24px; margin: 0; } h1 span { font-size: clamp(34px,9.5vw,60px); }
  .anniversary-label { order: 0; justify-self: start; font-size: 8px; padding: 8px; margin-bottom: 0; }
  .hero-intro { order: 2; font-size: 17px; }
  .hero-links { order: 4; gap: 18px; margin-top: 0; }
  .search-form { order: 5; margin-top: 0; }
  .search-field input { font-size: 16px; }
  .hero-art { order: 3; display: flex; align-items: center; justify-content: center; gap: 23px; border-block: 1px solid #709735; padding: 23px 0; }
  .hero-art > img { width: 112px; flex-shrink: 0; }
  .hero-art figcaption { margin-top: 0; text-align: left; font-size: 11px; max-width: 140px; }
  .hero-art figcaption .eyebrow { font-size: 10px; }
  .art-dates { font-size: 13px; }
  .anniversary-seal { display: none; }
  .feature-grid { grid-template-columns: 1fr; gap: 20px; }
  .feature { min-height: 150px; }
  .feature h2 { font-size: 17px; }
}
</style>
